import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const STORAGE_KEY = 'fig_saved_roles';

const readStorage = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

const writeStorage = (roles) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(roles)); } catch {}
};

/**
 * Manages saved/bookmarked career roles.
 * localStorage is the primary store so saves are instant + persistent.
 * Supabase is synced in the background when available.
 */
export const useSavedRoles = (user) => {
  const [savedRoles, setSavedRolesState] = useState(readStorage);

  const setSavedRoles = (roles) => {
    setSavedRolesState(roles);
    writeStorage(roles);
  };

  // On mount, try to merge Supabase data with localStorage
  useEffect(() => {
    if (!user) return;
    supabase
      .from('saved_roles')
      .select('role_title, role_data, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) return;
        const remote = data.map(d => ({ title: d.role_title, ...d.role_data }));
        // Merge: remote takes precedence, keep any local-only items too
        const local = readStorage();
        const localTitles = new Set(remote.map(r => r.title));
        const localOnly = local.filter(r => !localTitles.has(r.title));
        const merged = [...remote, ...localOnly];
        setSavedRoles(merged);
      });
  }, [user]);

  const saveRole = (role) => {
    const title = role.title;
    const current = readStorage();
    if (current.some(r => r.title === title)) return; // already saved

    const updated = [{ ...role, title }, ...current];
    setSavedRoles(updated);

    if (user) {
      supabase.from('saved_roles')
        .insert({ user_id: user.id, role_title: title, role_data: role })
        .then(({ error }) => {
          if (error) console.warn('Supabase sync failed (data kept locally):', error.message);
        });
    }
  };

  const removeRole = (title) => {
    const updated = readStorage().filter(r => r.title !== title);
    setSavedRoles(updated);

    if (user) {
      supabase.from('saved_roles')
        .delete()
        .eq('user_id', user.id)
        .eq('role_title', title)
        .then(({ error }) => {
          if (error) console.warn('Supabase delete failed:', error.message);
        });
    }
  };

  const isSaved = (title) => savedRoles.some(r => r.title === title);

  return { savedRoles, saveRole, removeRole, isSaved };
};
